<template>
  <form class="text-center">
    <VueFormGenerator 
      ref="workerSignUpForm"
      :schema="schema" 
      :model="worker" 
      :options="formOptions"
      :is-new-model="true"
    />

    <button 
      type="button" 
      class="button mt-5" 
      :disabled="submitDisabled" 
      @click="onSubmit"
    >
      {{ $translations.logInSignUp.sign_up_submit }}
    </button>
  </form>
</template>

<script>
  import { api, getFormLabel, passwordValidation, validateField, onFieldValidated } from '@/helpers';
  import { regexes, uploadMaxSizes, toasters } from '@/constants';

  export default {
    name: 'WorkerSignUpForm',
    props: {
      hiddenFields: {
        type: Array,
        default: () => []
      }
    },
    data: () => {
      return {
        worker: {
          acceptedTerms: false,
          avatar: null,
          email: null,
          firstname: null,
          lastname: null,
          locality: null,
          password: null,
          passwordConfirmation: null,
          phoneNumber: null,
          role: 'worker'
        },
        termsPath: null,
        schema: {},
        showWarning: false,
        formOptions: {
          validateAfterChanged: true,
          validateDebounceTime: 400 // Time before field validation starts after input
        }
      };
    },
    computed: {
      validatedFields() {
        return this.schema.fields ? this.schema.fields.filter(f => f.customOptions.isValid) : [];
      },
      submitDisabled() {
        return this.schema.fields ? this.validatedFields.length !== this.schema.fields.length : true;
      }
    },
    mounted() {
      this.fetchLastestTerms();
    },
    methods: {
      async fetchLastestTerms() {
        const response = await api.get('/terms');

        if (response.data) {
          this.termsPath = response.data.file_url;
        }

        this.setSchema();
      },
      setSchema() {
        this.schema = {
          fields: [
            {
              type:'imageUpload',
              inputName: 'avatar',
              model: 'avatar',
              required: false,
              hint: this.$t('shared.max_file_upload', { weight: uploadMaxSizes.avatar.toMo() }),
              styleClasses: ['field-image-upload mx-auto'],
              onImageChange: (img) => {
                this.worker.avatar = img;
              },
              customOptions: {
                isValid: true,
                maxFileUpload: uploadMaxSizes.avatar.bytes
              }
            },
            {
              type: 'label',
              label: getFormLabel('label_input', 'required_fields'),
              styleClasses: ['text-left text-danger italic text-sm red required-legend'],
              customOptions: {
                isValid: true,
              }
            },
            {
              type: 'input',
              inputType: 'text',
              inputName: 'firstname',
              label: getFormLabel('firstname', 'label'),
              model: 'firstname',
              styleClasses: ['input'],
              required: true,
              styleClasses: ['home-input'],
              validator: 'string',
              onValidated: (m, e, f) => onFieldValidated(e, f),
              customOptions: {
                isValid: false
              }
            },
            {
              type: 'input',
              inputType: 'text',
              inputName: 'lastname',
              label: getFormLabel('lastname', 'label'),
              model: 'lastname',
              required: true,
              styleClasses: ['home-input'],
              validator: 'string',
              onValidated: (m, e, f) => onFieldValidated(e, f),
              customOptions: {
                isValid: false
              }
            },
            {
              type: 'input',
              inputType: 'email',
              inputName: 'email',
              label: getFormLabel('email', 'label'),
              model: 'email',
              autocomplete: 'username',
              required: true,
              styleClasses: ['home-input'],
              validator: 'email',
              onValidated: (m, e, f) => onFieldValidated(e, f),
              customOptions: {
                isValid: false
              }
            },
            {
              type: 'input',
              inputType: 'password',
              inputName: 'password',
              label: getFormLabel('password', 'label'),
              model: 'password',
              min: 6,
              autocomplete: 'new-password',
              required: true,
              hint: getFormLabel('password', 'hint'),
              styleClasses: ['home-input'],
              validator: ['string', (value, field, model) => {
                if (model.passwordConfirmation && model.passwordConfirmation.length > 0) {
                  return model.passwordConfirmation !== model.password ? 
                    [getFormLabel('password_confirmation', 'not_matching')] : [];
                } else {
                  return [];
                }
              }],
              onChanged: () => passwordValidation(this.$refs.workerSignUpForm.$children,
                                                  'password',
                                                  this.worker.password,
                                                  this.worker.passwordConfirmation),
              onValidated: (m, e, f) => onFieldValidated(e, f),
              customOptions: {
                isValid: false
              }
            },
            {
              type: 'input',
              inputType: 'password',
              inputName: 'passwordConfirmation',
              label: getFormLabel('password_confirmation', 'label'),
              model: 'passwordConfirmation',
              autocomplete: 'new-password',
              required: true,
              styleClasses: ['home-input'],
              validator: (value, field, model) => {
                return model.password !== model.passwordConfirmation ? 
                  [getFormLabel('password_confirmation', 'not_matching')] : [];
              },
              onChanged: () => passwordValidation(this.$refs.workerSignUpForm.$children,
                                                  'passwordConfirmation',
                                                  this.worker.password,
                                                  this.worker.passwordConfirmation),
              onValidated: (m, e, f) => onFieldValidated(e, f),
              customOptions: {
                isValid: false
              }
            },
            {
              type: 'googlePlaces',
              inputName: 'locality',
              label: getFormLabel('city', 'label'),
              model: 'locality',
              placeholder: getFormLabel('city', 'placeholder'),
              required: true,
              styleClasses: ['home-input'],
              validator: 'string',
              onValidated: (m, e, f) => onFieldValidated(e, f),
              customOptions: {
                id: 'city',
                types: '(cities)',
                country: 'fr',
                isValid: true,
                inputChanged: (value) => this.onLocalityChange(value, 'locality')
              }
            },
            {
              type: 'label',
              label: getFormLabel('label_input', 'wrong_locality'),
              styleClasses: ['text-left text-danger italic text-sm red required-legend'],
              visible: () => {
                return this.showWarning;
              },
              customOptions: {
                isValid: true,
              }
            },
            {
              type: 'input',
              inputType: 'phone',
              inputName: 'phoneNumber',
              label: getFormLabel('phone', 'label'),
              model: 'phoneNumber',
              required: true,
              validator: (value) => {
                return regexes.digitsRegex.test(value) ? 
                  '' : getFormLabel('phone', 'not_matching');
              },
              onValidated: (m, e, f) => onFieldValidated(e, f),
              styleClasses: ['home-input'],
              customOptions: {
                isValid: false
              }
            },
            {
              type: 'checkbox',
              inputName: 'acceptedTerms',
              label: `${getFormLabel('terms', 'accept_label')} 
                <a class="font-bold" href="${this.termsPath}" download>${getFormLabel('terms', 'terms')}</a> 
                ${getFormLabel('terms', 'app')}`,
              model: 'acceptedTerms',
              required: true,
              validator: (value) => {
                return value ? [] : [''];
              },
              onValidated: (m, e, f) => onFieldValidated(e, f),
              customOptions: {
                isValid: false
              }
            }
          ]
        };
      },
      onLocalityChange(value, modelKey) {
        this.showWarning = value && value.locality.length ? false : true;
        this.worker.locality = value && value.locality.length ? value.locality : null;
        validateField(this.$refs.workerSignUpForm.$children, modelKey);
      },
      onSubmit() {
        if (!this.worker.locality) this.showWarning = true;

        if (!this.submitDisabled && this.worker.locality) {
          // Prevent form submit if disabled attributes was maliciously changed
          this.$emit('on-submit', this.worker);
        } else {
          toasters.triggerError();
        }
      }
    }
  };
</script>
